import { memo, useRef, useCallback, useMemo, useEffect } from "react";
import { css, Global } from "@emotion/react";
import {
  useVisualizationState,
  VisualizationContent,
} from "@atlas-int/viz/shared";
import { VisualizationContentContainer } from "../../../VizGrid";
import { TradeDirection, TradeFlow, ProductClass, LocationDetailLevel } from "@atlas-int/data/types";
import { OverTimeLayoutOption, OverTimeOrderingOption } from "../constants";
import { Tooltip } from "@atlas-int/viz/shared";
import { GenericResizeContainer } from "@atlas-int/viz/shared";
import { useOvertimeTooltip, useTooltipElements } from "../hooks";
import Chart from "./Chart";
import { ChartQueryVariables, MappedSeriesData } from "../types";
import type { YearRangeForProductClass } from "../types";
import type { MetadataFetchState, LocationMetadataPayload, LocationYearDatum, DeflatorDatum } from "@atlas-int/data/types";
import type { ProductMetadataBundle } from "@atlas-int/data/types";
import { UIView } from "@atlas-int/data";
import type { ExploreDataRecord } from "@atlas-int/data";
import type { ReactElement } from "react";
import type { TooltipDatum } from "@atlas-int/viz/shared";

interface ChartWithTooltipProps {
  inputData: { data: ExploreDataRecord[] } | undefined;
  error: Error | undefined;
  loading: boolean;
  exporter: string | undefined;
  importer: string | undefined;
  yearRangeForProductClass: YearRangeForProductClass | null;
  chartQueryVariables: ChartQueryVariables;
  hiddenCategories: string[];
  tradeDirection: TradeDirection;
  tradeFlow: TradeFlow;
  locationMetadata: MetadataFetchState<LocationMetadataPayload>;
  productMetadata: ProductMetadataBundle;
  locationYearMetadata: MetadataFetchState<LocationYearDatum[]>;
  deflatorsMetadata: MetadataFetchState<DeflatorDatum[]>;
  highlightedItem: string | undefined;
  setHighlightedItem: (item: string | undefined) => void;
  view: UIView;
  totalValue: number | undefined;
  productClass: ProductClass;
  yAxis: string | undefined;
  overTimeLayout: OverTimeLayoutOption;
  overTimeOrdering: OverTimeOrderingOption;
  locationLevel: LocationDetailLevel;
  onYAxisChange?: (yAxis: string) => void;
  /** Stable memo() component for rendering tooltip content. */
  TooltipContent?: React.ComponentType<{
    datum: TooltipDatum;
    view: UIView;
    totalValue: number;
    tradeDirection: TradeDirection;
    productClass: ProductClass;
    fillColor: string;
  }>;
}

/**
 * Isolates overtime tooltip behavior from ConnectedOvertime (legend, query wiring).
 * Uses `useOvertimeTooltip` for snap/series behavior instead of the shared
 * `TooltipStateIsolationProvider` + `TooltipStateBridge` + `useTooltipDispatchRef` stack
 * used by geomap, treemap, and market share.
 */
const ChartWithTooltip = memo(
  ({
    inputData,
    error,
    loading,
    exporter,
    importer,
    yearRangeForProductClass,
    chartQueryVariables,
    hiddenCategories,
    tradeDirection,
    tradeFlow,
    locationMetadata,
    productMetadata,
    locationYearMetadata,
    deflatorsMetadata,
    highlightedItem,
    setHighlightedItem,
    view,
    totalValue,
    productClass,
    yAxis,
    overTimeLayout,
    overTimeOrdering,
    locationLevel,
    onYAxisChange,
    TooltipContent,
  }: ChartWithTooltipProps) => {
    const overtimeElement = useRef<HTMLDivElement | null>(null);
    const mappedSeriesDataRef = useRef<MappedSeriesData[]>([]);

    const onSetSeriesData = useCallback((data: MappedSeriesData[]) => {
      mappedSeriesDataRef.current = data;
    }, []);

    const clearHighlightedItemCallback = useCallback(() => {
      setHighlightedItem(undefined);
    }, [setHighlightedItem]);

    const {
      tooltipDisplay,
      chartContainerOffset,
      snapTooltipToCoordinates,
      onShowTooltip,
      onHideTooltip,
      setXAxisTooltipValue,
      updateChartContainerOffset,
      clearHighlightedItem,
    } = useOvertimeTooltip(
      highlightedItem,
      mappedSeriesDataRef,
      clearHighlightedItemCallback,
    );

    useEffect(() => {
      const node = overtimeElement.current;
      if (!node) return;

      const updateOffset = () => updateChartContainerOffset(node);
      updateOffset();

      const resizeObserver = new ResizeObserver(() => updateOffset());
      resizeObserver.observe(node);

      window.addEventListener("scroll", updateOffset, true);
      window.addEventListener("resize", updateOffset);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("scroll", updateOffset, true);
        window.removeEventListener("resize", updateOffset);
      };
    }, [updateChartContainerOffset]);

    const { tooltipContentElement, tooltipLineMarkerElement } =
      useTooltipElements({
        tooltipDisplay,
        chartContainerOffset,
        view,
        totalValue,
        tradeDirection: tradeDirection as string,
        productClass,
        yAxis,
        tradeFlow,
        isLoading: loading,
        overtimeElement,
        TooltipContent,
      });

    const vizState = useVisualizationState({
      loading,
      error,
      data: inputData,
      previousData: undefined,
    });

    // Memoize the chart content to prevent re-renders on tooltip state changes
    const chart = useMemo(() => {
      const hasValidSelection = exporter || importer;

      if (hasValidSelection && inputData && yearRangeForProductClass) {
        return (
          <Chart
            inputData={inputData}
            queryVariables={chartQueryVariables}
            yearRangeForProductClass={yearRangeForProductClass}
            hiddenCategories={hiddenCategories}
            tradeDirection={tradeDirection}
            tradeFlow={tradeFlow}
            locationMetadata={locationMetadata}
            productMetadata={productMetadata}
            locationYearMetadata={locationYearMetadata}
            deflatorsMetadata={deflatorsMetadata}
            isLoading={loading}
            onShowTooltip={onShowTooltip}
            onHideTooltip={onHideTooltip}
            highlightedItem={highlightedItem}
            onSetSeriesData={onSetSeriesData}
            setXAxisTooltipValue={setXAxisTooltipValue}
            overTimeLayout={overTimeLayout}
            overTimeOrdering={overTimeOrdering}
            locationLevel={locationLevel}
            onYAxisChange={onYAxisChange}
          />
        );
      }
      return null;
    }, [
      inputData,
      exporter,
      importer,
      yearRangeForProductClass,
      chartQueryVariables,
      hiddenCategories,
      tradeDirection,
      tradeFlow,
      locationMetadata,
      productMetadata,
      locationYearMetadata,
      deflatorsMetadata,
      loading,
      onShowTooltip,
      onHideTooltip,
      highlightedItem,
      onSetSeriesData,
      setXAxisTooltipValue,
      locationLevel,
      overTimeLayout,
      overTimeOrdering,
      onYAxisChange,
    ]);

    return (
      <GenericResizeContainer chartContainerRef={overtimeElement}>
        <Global
          styles={css`
            [data-highlighted-item]:not([data-highlighted-item=""]) {
              [data-ribbon-key] {
                opacity: 0.4;
                transition: opacity 200ms ease;
              }
              [data-ribbon-key="${highlightedItem}"] {
                opacity: 1;
              }
            }
          `}
        />
        <Tooltip
          explanation={tooltipContentElement}
          cursor="default"
          overrideStyles={true}
          clearHighlightedItem={clearHighlightedItem}
          isFixed={false}
          isFixedCoords={snapTooltipToCoordinates}
          snapToPosition={true}
          snapDirection="above"
          persistOnMouseLeave={!!highlightedItem}
        >
          <VisualizationContentContainer
            ref={overtimeElement}
            style={{ position: "relative" }}
            data-highlighted-item={highlightedItem || ""}
          >
            <VisualizationContent
              hasError={vizState.hasError}
              errorNoticeType={vizState.errorNoticeType}
              hasNoData={vizState.hasNoData}
              loading={loading}
            >
              {chart}
              {!loading && tooltipLineMarkerElement}
            </VisualizationContent>
          </VisualizationContentContainer>
        </Tooltip>
      </GenericResizeContainer>
    );
  },
);

ChartWithTooltip.displayName = "ChartWithTooltip";

export default ChartWithTooltip;
